import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import { URL_API } from '../config';
// Fallback local : snapshot de la table `translations` (format { records: [...] }).
// Sert si l'API est injoignable, AVANT même tout premier chargement réussi.
import fallbackTranslations from './translations.fallback.json';

const TRANSLATIONS_URL = `${URL_API}api.php/records/translations`;
// Cache de la dernière version chargée avec succès (pour rester à jour hors-ligne).
const CACHE_KEY = 'translations_cache_v1';

const LANGS = ['fr', 'en', 'de'] as const;

const safeGetCache = (): string | null => {
  try {
    return localStorage.getItem(CACHE_KEY);
  } catch {
    return null;
  }
};

const safeSetCache = (text: string) => {
  try {
    localStorage.setItem(CACHE_KEY, text);
  } catch {
    /* localStorage indispo : on ignore */
  }
};

// Transforme le format API { records: [{key, fr, en, de}] } en table i18next
// { "ma.cle": "valeur" } pour une langue donnée.
// Si la valeur de la langue demandée est vide/null, on retombe sur EN puis FR
// pour ne JAMAIS renvoyer la clé brute. Une clé absente partout est omise
// (i18next appliquera alors `fallbackLng`).
const parseRecordsForLang = (raw: string, lang: string) => {
  const records: any[] = JSON.parse(raw).records;
  const table: any = {};
  for (const rec of records) {
    const value = rec[lang] || rec.en || rec.fr;
    if (value) table[rec.key] = value;
  }
  return table;
};

// Ressources disponibles dès le démarrage, de façon SYNCHRONE, pour qu'aucune
// clé brute (ex: "hero.subtitle") ne s'affiche le temps que l'API réponde sur
// un appareil neuf / connexion lente. Source : cache local sinon snapshot bundlé.
const bootstrapResources = (() => {
  const raw = safeGetCache() ?? JSON.stringify(fallbackTranslations);
  const resources: any = {};
  for (const lang of LANGS) {
    resources[lang] = { translations: parseRecordsForLang(raw, lang) };
  }
  return resources;
})();

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: TRANSLATIONS_URL,
      crossDomain: true,
      // Chargement custom : 1) API  2) cache localStorage  3) fallback bundlé.
      // Quelle que soit la source, on renvoie la même forme JSON -> le `parse`
      // ci-dessous s'applique de façon identique.
      request: (
        _options: any,
        url: string,
        _payload: any,
        callback: (err: any, res: { status: number; data: string }) => void
      ) => {
        fetch(url)
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.text();
          })
          .then((text) => {
            safeSetCache(text); // on garde la dernière version OK
            callback(null, { status: 200, data: text });
          })
          .catch((e) => {
            // API KO -> cache si dispo, sinon fallback embarqué
            const cached = safeGetCache();
            const data = cached ?? JSON.stringify(fallbackTranslations);
            // eslint-disable-next-line no-console
            console.warn(
              `[i18n] Traductions API indisponibles, utilisation du ${
                cached ? 'cache local' : 'fallback embarqué'
              }.`,
              e
            );
            callback(null, { status: 200, data });
          });
      },
      parse: (data: any, lang: any) => parseRecordsForLang(data, lang),
    },
    // Préchargé de façon synchrone (cache/fallback) : garantit des traductions
    // dès le 1er rendu. Le backend HTTP rafraîchira ensuite en arrière-plan.
    resources: bootstrapResources,
    // On a fourni des ressources, mais elles peuvent être partielles -> autorise
    // quand même le backend à charger/compléter depuis l'API.
    partialBundledLanguages: true,
    // Normalise la langue détectée AVANT stockage : "fr-CH"/"fr-FR" -> "fr",
    // "de-CH" -> "de", etc. Garantit que `i18nextLng` (localStorage) contienne
    // toujours "fr"/"en"/"de" et reste cohérent avec le sélecteur (useLocales).
    detection: {
      convertDetectedLanguage: (lng: string) => lng.split('-')[0],
    },
    // Langues réellement disponibles : on empêche i18next de tenter de charger
    // une langue système non supportée (es, it, pt...) qui afficherait les clés.
    supportedLngs: ['fr', 'en', 'de'],
    // Si le téléphone est dans une autre langue, on retombe sur l'anglais.
    fallbackLng: 'en',
    // Évite que la variante régionale (en-US, fr-CH...) parte chercher un fichier
    // séparé : on normalise vers la langue de base.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
